/**
 * This component contain the Submission Page.
 * A submission page consist of multiple steps.
 * The progress at each stage will be stored
 *   - locally (in memory) and remotely (POST),
 *
 * Submission wrapper should handle all the mutation.
 * All of its child components shall simply dispatch a request for updating.
 * This will imply that
 *  - data will always flow downwards in one direction only.
 *  - child components should not directly mutate the submission store
 *     (i.e. child component is not depended on store)
 *
 * Update from child should only be obtained from event `updateData`.
 */

<template>

  <el-container class="submission">
    <el-header>
      <el-col
        :xs="24"
        class="submission__component submission__component--steps">
        <!-- TODO: This is not mobile responsive, may need alternative design for mobile -->
        <NavigationSteps
          :videoId="videoId"
          :seen="seen"
        />
      </el-col>
    </el-header>

    <el-main>
      <div class="content-width">
        <el-row
          justify="center"
          type="flex">
          <el-col
            class="submission__component submission__component--main">
            <el-alert v-if="hasComment" show-icon type="info" :title="submission.comments" :closable="false"></el-alert>

            <sh-spacer by="0.5"/>

            <keep-alive>
              <router-view
                ref="currentStepComponent"
                :value="submission"
                :uploadQueue="uploadQueue"
                @input="updateData"
              />
            </keep-alive>
          </el-col>
        </el-row>
      </div>
    </el-main>
    <el-footer class="submission__component submission__component--footer">
      <div class="content-width" style="height: 100%;">
        <NavigationButtons
          :submittable="submittable"
          :seen="seen"
          @submit="submit"
        />
      </div>
    </el-footer>

    <PostSubmissionDialog :visible="showPostSubmissionDialog" />

  </el-container>

</template>

<script>
import store from '@/store';
import SUBMISSION_STATUS from '@/assets/data/viddsee/submissionStatus';
import NOTIFY_CONSTANTS from '@/config/notify-constants';
import NavigationSteps from './Shared/NavigationSteps';
import NavigationButtons from './Shared/NavigationButtons';
import PostSubmissionDialog from './Shared/PostSubmissionDialog';
import VideoUpload from './steps/VideoUpload';

export default {
  components: {
    VideoUpload,
    NavigationButtons,
    NavigationSteps,
    PostSubmissionDialog,
  },
  data() {
    return {
      videoId: parseInt(this.$route.params.videoId),
      seen: [],
      showPostSubmissionDialog: false,
    };
  },
  computed: {
    // Model
    submission() {
      return this.$store.state.submission[this.videoId];
    },
    uploadQueue() {
      const queue = this.$store.state.uploadQueue[this.videoId] || {};
      return queue.video;
    },
    submittable() {
      if (this.videoId) {
        const isAllValid = this.$store.getters['submissionProgress/submittable'](this.videoId);
        const hasOnGoingUpload = this.uploadQueue;
        return isAllValid && !hasOnGoingUpload;
      } else {
        return false;
      }
    },
    submissionStatus() {
      if (this.submission) {
        return SUBMISSION_STATUS.TYPE[this.submission.status];
      } else {
        return SUBMISSION_STATUS.TYPE.pending;
      }
    },
    hasComment() {
      if (this.submission) {
        return this.submissionStatus === SUBMISSION_STATUS.TYPE.moreinfo && this.submission.comments;
      } else {
        return false;
      }
    },
  },
  methods: {
    // Model
    updateData(data = {}) {
      // TODO: Implement error notification / fallback
      this.$store.commit('networkActivity/loading');
      this.$store.dispatch('submission/update', {
        id: this.videoId,
        ...data,
      })
        .then(() => {
          this.$store.commit('networkActivity/success');
          store.dispatch('submissionProgress/update', this.submission.id);
        })
        .catch((error) => {
          this.$notify.error({
            title: 'Error',
            message: (error.data && error.data.error) || error.message,
            duration: NOTIFY_CONSTANTS.DURATION.MEDIUM,
          });
          this.$store.commit('networkActivity/error');
          throw error;
        });
    },
    submit() {
      if (window.userRefusedToUpdate) {
        this.$confirm(
          'There is a pending update to the website. Please refresh your page before submitting.',
          'Refresh',
          {
            confirmButtonText: 'Refresh',
            cancelButtonText: 'Not now',
            type: 'warning',
          }
        ).then(() => {
          location.reload();
        });
        return;
      }
      this.$confirm(`You are about to finalize the submission. 
      Press Submit to acknowledge that your film will be published online on <strong>Viddsee.com</strong> if approved.`,
      'Confirm submission?',
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: 'Submit',
        cancelButtonText: 'Not now',
        type: 'warning',
      }
      ).then(() => {
        const loading = this.$loading({
          lock: true,
          text: 'Submitting',
          background: 'rgba(0, 0, 0, 0.7)',
        });
        this.$store.dispatch('submission/submit', this.submission)
          .then(() => {
            this.handlePostSubmission();
            loading.close();
          }).catch((error) => {
            loading.close();
            this.$notify.error({
              title: 'Error',
              message: (error.data && error.data.error) || error.message,
              duration: NOTIFY_CONSTANTS.DURATION.LONG,
            });
            throw error;
          });
      });
    },

    handlePostSubmission() {
      this.showPostSubmissionDialog = true;
    },

    // Local State
    updateSeen(route) {
      const stepIndex = parseInt(route.path.split('/').splice(-1)[0], 10) - 1;
      this.$set(this.seen, stepIndex, true);
    },
  },
  mounted() {
    store.dispatch('submissionProgress/update', this.submission.id);
    this.seen = (this.submissionStatus === SUBMISSION_STATUS.TYPE.incomplete) ? [] : Array(6).fill(true);
    this.updateSeen(this.$route);
  },
  beforeRouteUpdate(to, from, next) {
    this.updateSeen(to);
    next();
  },
  beforeRouteEnter(to, from, next) {
    // Proceed if submission already contain id
    if (to.params.videoId) {
      store.dispatch('submission/get', to.params.videoId)
        .then(() => next());
    // Else, request for a new submission
    } else {
      store.dispatch('submission/create')
        .then((submission) => {
          next(`${to.path}/${submission.id}`);
        })
        .catch((error) => {
          next(false);
          throw error;
        });
    }
  },
};
</script>

<style lang="scss">
.submission {
  height: calc(100vh - 60px);
  margin: -$--main-padding; //
}
.submission__component {
  margin-bottom: 2em;
}

.submission__component.submission__component--footer {
  box-shadow: 0 0px 1px 1px rgba(0, 0, 0, 0.05);
  margin-bottom: 0;
  position: relative;
  z-index: 0;
}

.submission {
  .el-input.is-disabled .el-input__inner,
  .el-textarea.is-disabled .el-textarea__inner {
    background-color: #ffffff;
    border-color: #e8e8e8;
    color: #777777;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Submission/Wrapper.vue